import AppSettings from "../../../config";
import ArcgisConfig from "@arcgis/core/config";
import * as locator from "@arcgis/core/rest/locator";
import { coreService } from "../../../services/coreService";
import { buildQuery } from "../../../lib/network";
import { ArgumentTypes, FirstArgumentType, SecondArgumentType } from "../../../types/ArgumentTypes";
import SpatialReference from "@arcgis/core/geometry/SpatialReference";
import Point from "@arcgis/core/geometry/Point";

export const ARCGIS_GEOCODE_URL = "https://geocode.arcgis.com/arcgis/rest/services/World/GeocodeServer";
export async function geocode(address: string) {
  ArcgisConfig.apiKey = AppSettings.ARCGIS_API_KEY;
  return await locator.addressToLocations(ARCGIS_GEOCODE_URL, {
    address: { SingleLine: address },
    maxLocations: 1,
    outFields: ["location"],
    outSpatialReference: SpatialReference.WGS84,
    forStorage: true,
  });
}

export async function reverseGeocode(lat: number, lng: number) {
  try {
    ArcgisConfig.apiKey = AppSettings.ARCGIS_API_KEY;
    const response = await locator.locationToAddress(ARCGIS_GEOCODE_URL, {
      location: new Point({
        longitude: lng,
        latitude: lat,
        spatialReference: SpatialReference.WGS84,
      }),
      outSpatialReference: SpatialReference.WGS84,
    });
    return response;
  } catch (error) {
    console.error("Reverse geocoding failed:", error);
    return null;
  }
}

export const getMapLayers = (headers: any) => () => {
  return buildQuery(coreService.mappingLayersList({ headers }), (response) => {
    return response.data;
  });
};

export type LayerFieldDefinition = NonNullable<
  Awaited<ReturnType<ReturnType<typeof getMapLayer>>>["fieldDefinitions"]
>[number];
export const getMapLayer = (headers: any) => (id: number | string) => {
  return buildQuery(coreService.mappingLayersDetail(+id, { headers }), (response) => {
    return response.data;
  });
};

export const createMapLayer = (headers: any) => (data: FirstArgumentType<typeof coreService.mappingLayersCreate>) => {
  return buildQuery(coreService.mappingLayersCreate(data, { headers }), (response) => {
    return response.data;
  });
};

export type LayerData = Awaited<ReturnType<typeof coreService.mappingLayersUpdate>>["data"][number];
export const updateMapLayer =
  (headers: any) =>
  ({ id, data }: { id: number; data: SecondArgumentType<typeof coreService.mappingLayersUpdate> }) => {
    return buildQuery(coreService.mappingLayersUpdate(id, data, { headers }), (response) => {
      return response.data;
    });
  };

export const deactivateMapLayer = (headers: any) => (id: number) => {
  headers = {
    ...headers,
    "Content-Type": "text/plain",
  };
  return buildQuery(coreService.mappingLayersDelete(id, { headers }), (response) => {
    return response.data;
  });
};

export const reactivateMapLayer = (headers: any) => (id: number) => {
  headers = {
    ...headers,
    "Content-Type": "text/plain",
  };
  return buildQuery(coreService.mappingLayersReactivate(id, { headers }), (response) => {
    return response.data;
  });
};

export const SystemLayerType = {
  LocationParcel: "LocationParcel",
} as const;
export type SystemLayerType = (typeof SystemLayerType)[keyof typeof SystemLayerType];
export const getSystemMapLayer = (headers: any) => (systemLayerType: SystemLayerType) => {
  return buildQuery(coreService.mappingLayersSystemList({ systemLayerType }, { headers }), (response) => {
    return response.data;
  });
};

export const getMapFeatures = (headers: any) => (id: number | string) => {
  return buildQuery(coreService.mappingLayersFeaturesList(+id, { headers }), (response) => {
    return response.data;
  });
};

export const getMapFeature =
  (headers: any) =>
  ({ layerId, featureId }: { layerId: number | string; featureId: number | string }) => {
    return buildQuery(coreService.mappingLayersFeaturesDetail(+layerId, +featureId, { headers }), (response) => {
      return response.data;
    });
  };

export const createMapFeature =
  (headers: any) =>
  ({
    layerId,
    data,
  }: {
    layerId: number | string;
    data: SecondArgumentType<typeof coreService.mappingLayersFeaturesCreate>;
  }) => {
    return buildQuery(coreService.mappingLayersFeaturesCreate(+layerId, data, { headers }), (response) => {
      return response.data;
    });
  };

export const deleteMapFeature =
  (headers: any) =>
  ({ layerId, featureId }: { layerId: number | string; featureId: number | string }) => {
    return buildQuery(coreService.mappingLayersFeaturesDelete(+layerId, +featureId, { headers }), (response) => {
      return response.data;
    });
  };

export const updateMapFeature =
  (headers: any) =>
  ({
    layerId,
    featureId,
    data,
  }: {
    layerId: number | string;
    featureId: number | string;
    data: ArgumentTypes<typeof coreService.mappingLayersFeaturesUpdate>[2];
  }) => {
    return buildQuery(coreService.mappingLayersFeaturesUpdate(+layerId, +featureId, data, { headers }), (response) => {
      return response.data;
    });
  };

export type BulkUpdateParams = ArgumentTypes<typeof coreService.mappingLayersFeaturesUpdateMany>[1];
export const bulkUpdateMapFeature =
  (headers: any) =>
  ({ layerId, data }: { layerId: number | string; data: BulkUpdateParams }) => {
    return buildQuery(coreService.mappingLayersFeaturesUpdateMany(+layerId, data, { headers }), (response) => {
      return response.data;
    });
  };

export type AdHocFeature = Awaited<ReturnType<typeof getAdhocFeature>>;
export const getAdhocFeatures = (headers: any) => () => {
  return buildQuery(coreService.mappingFeaturesList({ headers }), (response) => {
    return response.data;
  });
};

export const getAdhocFeature = (headers: any) => (id: number | string) => {
  return buildQuery(coreService.mappingFeaturesDetail(+id, { headers }), (response) => {
    return response.data;
  });
};

export const createAdhocFeature =
  (headers: any) => (data: FirstArgumentType<typeof coreService.mappingFeaturesCreate>) => {
    return buildQuery(coreService.mappingFeaturesCreate(data, { headers }), (response) => {
      return response.data;
    });
  };

export const updateAdhocFeature =
  (headers: any) => (id: number | string, data: SecondArgumentType<typeof coreService.mappingFeaturesUpdate>) => {
    return buildQuery(coreService.mappingFeaturesUpdate(+id, data, { headers }), (response) => {
      return response.data;
    });
  };

export type BulkAdhocUpdateParams = ArgumentTypes<typeof coreService.mappingFeaturesUpdateMany>[0];
export const bulkUpdateAdhocFeatures = (headers: any) => (data: BulkAdhocUpdateParams) => {
  return buildQuery(coreService.mappingFeaturesUpdateMany(data, { headers }), (response) => {
    return response.data;
  });
};

export type MapLayerSearchHit = Awaited<
  ReturnType<typeof coreService.mappingLayersSearchCreate>
>["data"]["hits"]["hits"][number]["_source"];
export const searchLayers =
  (headers: any) => (data: FirstArgumentType<typeof coreService.mappingLayersSearchCreate>) => {
    return buildQuery(coreService.mappingLayersSearchCreate(data, { headers }), (response) => {
      return response.data;
    });
  };

export type MapFeatureSearchHit = Awaited<
  ReturnType<typeof coreService.mappingFeaturesSearchCreate>
>["data"]["hits"]["hits"][number]["_source"];
export const searchFeatures =
  (headers: any) => (data: FirstArgumentType<typeof coreService.mappingFeaturesSearchCreate>) => {
    return buildQuery(coreService.mappingFeaturesSearchCreate(data, { headers }), (response) => {
      return response.data;
    });
  };
